import { deleteSavedSquad } from "@/api/privateRequests/squad-builder/delete-squad";
import { getSquads } from "@/api/privateRequests/squad-builder/get-squads";
import { saveSquad } from "@/api/privateRequests/squad-builder/save-squad";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { ClubImportRow } from "@/components/features/ClubImport/ClubImportRow";
import { Solving } from "@/components/organisms/SolutionView/SolveStatus";
import { HeroSection } from "@/containers/HeroSection";
import Toolbar from "@/containers/Toolbar";
import BottomSheet from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import Card from "@/design-system/Card/Card";
import { CheckBox, Tick } from "@/design-system/Check/Check";
import { ChemistryIcon } from "@/design-system/Chemistry/Chemistry";
import { Collapsible } from "@/design-system/Collapsible/Collapsible";
import { CustomizeButton } from "@/design-system/CustomizeButton/CustomizeButton";
import { FOLDOUT_HEADING, Foldout } from "@/design-system/Foldout/Foldout";
import { InfoTip } from "@/design-system/InfoTip/InfoTip";
import { Popover } from "@/design-system/Popover/Popover";
import { SegmentedControl } from "@/design-system/SegmentedControl/SegmentedControl";
import CloseButton from "@/design-system/SpecificButtons/CloseButton/CloseButton";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";
import { addAlert } from "@/redux/message/messageSlice";
import {
  DEFAULT_BUNDLE,
  changeBlockMode,
  changeObjectiveBundle,
  changeObjectiveProgress,
  changeObjectiveView,
  fetchObjectiveBundle,
  fetchStackedPlan,
  getObjectivesSelector,
  hasCustomPlanSettings,
  markSquadsSaved,
  pollObjectiveSquads,
  requestObjectiveSquads,
  revealPlan,
} from "@/redux/objectives/objectivesSlice";
import { AppDispatch, IRootState } from "@/redux/store";
import { BREAKPOINTS } from "@/settings";
import type {
  ObjectiveBundle,
  ObjectiveBundleChallengeStatus,
  ObjectivePlanView,
  ObjectiveSquadBlock,
  ObjectiveSquadSettings,
} from "@repo/shared/api";
import {
  backendErrorCode,
  errorMessage,
  useTranslation,
} from "@repo/shared/i18n";
import { formatTime } from "@repo/shared/utils";
import {
  ArrowRight,
  ChevronDown,
  Info,
  LoaderCircle,
  TriangleAlert,
  Trophy,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { useMediaQuery } from "usehooks-ts";

import { BundleGroupCard } from "./BundleGroupCard";
import { BundleRewards } from "./BundleRewards";
import {
  ObjectiveSquadView,
  objectiveSquadSaveRequest,
} from "./ObjectiveSquadView";
import { PlanSettings, PlanSettingsPanel, Stepper } from "./PlanSettingsPanel";
import {
  DEFAULT_SQUAD_SETTINGS,
  SquadSettingsPanel,
} from "./SquadSettingsPanel";
import {
  blockActions,
  eventSquadLines,
  lineupLines,
  roundLines,
} from "./objectivePlanFormat";

const TAG = "flex h-5 items-center rounded-full px-2 text-xs";

const MODES = [
  "squad-battles",
  "rivals",
  "champions",
  "champions-finals",
  "rush",
  "live-event",
  "draft",
  "co-op",
  "friendlies",
  "any",
] as const;

const STACKED_OPEN_KEY = "objectivePlan.stackedObjectivesOpen";
// The user's Customize Squads pick, kept across bundles.
const SQUAD_SETTINGS_KEY = "objectivePlan.squadSettings";

const DIFFICULTIES = [
  "beginner",
  "amateur",
  "semi-pro",
  "professional",
  "world-class",
  "legendary",
  "ultimate",
] as const;

const KINDS = [
  "match",
  "sbc",
  "market",
  "club",
  "rush",
  "moments",
  "objective",
  "placement",
  "watch",
  "other",
] as const;

const NOT_PLANNABLE = [
  "infeasible-for-club",
  "infeasible-with-cap",
  "unsupported",
  "requires-group",
  "requires-entry",
  "mode-excluded",
] as const satisfies ObjectiveBundleChallengeStatus[];

type Challenge = ObjectiveBundle["objectives"][number];

const SECURED = [
  "planned",
  "completed",
  "completed-by-reference",
  "side-task",
] as const satisfies ObjectiveBundleChallengeStatus[];

const SQUAD_POLL_MS = 3000;

/** A caret row that folds its children away; closed by default. */
const Expander = ({
  label,
  open,
  onToggle,
  children,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-y-2">
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="flex items-center gap-x-1 self-start text-sm text-foreground-muted hover:text-foreground-body"
    >
      {label}
      <ChevronDown
        size={16}
        className={cn(
          "transition-transform duration-200",
          !open && "-rotate-90",
        )}
      />
    </button>
    <Collapsible open={open}>{children}</Collapsible>
  </div>
);

/** The tick and its text as one button: clicking either toggles it. */
const TickButton = ({
  checked,
  disabled,
  onClick,
  className,
  children,
}: {
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-pressed={checked}
    disabled={disabled}
    onClick={onClick}
    className={cn(
      "flex items-center gap-x-3 text-left text-sm disabled:opacity-50",
      className,
    )}
  >
    <span className="flex size-5 shrink-0 items-center justify-center">
      <CheckBox
        checked={checked}
        className={cn(!checked && "border-neutral-600")}
      />
    </span>
    {children}
  </button>
);

type StepState = "waiting" | "ready" | "done";

/** One step of the game plan guide, led by an arrow from the step before. */
const Step = ({
  first = false,
  small = false,
  children,
}: {
  first?: boolean;
  small?: boolean;
  children: React.ReactNode;
}) => (
  <li className={cn("flex items-center", small ? "gap-x-1.5" : "gap-x-2")}>
    {!first && (
      <ArrowRight
        className={cn(
          "shrink-0 text-foreground-muted",
          small ? "size-3.5" : "size-4",
        )}
      />
    )}
    {children}
  </li>
);

/**
 * A guide step's button. Only a ready step clicks; waiting and done steps keep
 * the button but grey their text, done ones with a tick. `magic` makes the step
 * a magic button (no Credit, so no holo), disabled while it waits.
 */
const StepButton = ({
  state,
  magic = false,
  small = false,
  onClick,
  children,
}: {
  state: StepState;
  magic?: boolean;
  small?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Button
    variant={magic && state !== "done" ? "magic" : "tertiary"}
    size={small ? "small" : "medium"}
    aria-disabled={state !== "ready"}
    onClick={state === "ready" ? onClick : undefined}
    className={cn(state !== "ready" && "cursor-default", small && "text-xs")}
  >
    <span className="flex items-center gap-x-1.5">
      {state === "done" && <Tick className="text-streamline-success" />}
      <span className={cn(state !== "ready" && "text-neutral-500")}>
        {children}
      </span>
    </span>
  </Button>
);

const Section = ({
  title,
  action,
  children,
}: {
  title: string;
  /** Right of the title. */
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <section className="flex flex-col gap-y-3">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {action}
    </div>
    {children}
  </section>
);

const CLUB_IMPORT_ORIGIN = { section: "Objectives", subsection: "Plan" };

// Same inset as the toolbar, so the overview and the plan line up with it.
const CONTAINER = "mx-auto w-full max-w-content px-4 large:px-16";

const ObjectivePlanPage = () => {
  const { t, i18n } = useTranslation("objectives");
  const { bundleId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { currentBundle, build } = useSelector(getObjectivesSelector);
  const formations = useSelector(
    (state: IRootState) => state.loadedContentReducer.formations,
  );
  const [savingSquads, setSavingSquads] = useState(false);
  // The view whose saved squads are being refreshed, whether the server
  // started the new run (until then the old run still reads as done), and
  // whether the refreshed squads were saved over the old ones.
  const [refresh, setRefresh] = useState<{
    view: ObjectivePlanView;
    started: boolean;
    updated: boolean;
  }>();
  // The block whose squads the sheet shows, opened on squad `index`.
  const [shownSquads, setShownSquads] = useState<{
    squads: Extract<ObjectiveSquadBlock, { status: "done" }>["squads"];
    index: number;
  }>();
  const goBack = useGoBack({ fallback: "/objectives" });
  const navigate = useNavigate();
  // The completion pop-up, put away until the page is left.
  const [completionDismissed, setCompletionDismissed] = useState(false);
  const isTouch = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  // Opened carets and reopened finished games, by `${view}-${kind}-${index}`.
  const [open, setOpen] = useState<Set<string>>(new Set());
  // Blocks the user opened or closed, valid only while `current` (view and
  // current block) holds: once a block is completed, the next one opens alone.
  const [blockToggles, setBlockToggles] = useState<{
    current: string;
    open: Record<number, boolean>;
  }>({ current: "", open: {} });
  // Games the user opened or closed, the same way: keyed by the current game.
  const [gameToggles, setGameToggles] = useState<{
    current: string;
    open: Record<number, boolean>;
  }>({ current: "", open: {} });
  const toggleOpen = (key: string) =>
    setOpen((keys) => {
      const next = new Set(keys);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  const [storedSquadSettings, setStoredSquadSettings] = useState<
    ObjectiveSquadSettings | undefined
  >(() => {
    try {
      const raw = localStorage.getItem(SQUAD_SETTINGS_KEY);
      return raw
        ? { ...DEFAULT_SQUAD_SETTINGS, ...JSON.parse(raw) }
        : undefined;
    } catch {
      return undefined;
    }
  });
  const changeSquadSettings = (next: ObjectiveSquadSettings) => {
    setStoredSquadSettings(next);
    try {
      localStorage.setItem(SQUAD_SETTINGS_KEY, JSON.stringify(next));
    } catch {
      // The pick still holds for this visit.
    }
  };
  // Stacked Objectives starts closed; its open state is remembered across visits.
  const [stackedOpen, setStackedOpen] = useState(() => {
    try {
      return localStorage.getItem(STACKED_OPEN_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleStackedOpen = () => {
    try {
      localStorage.setItem(STACKED_OPEN_KEY, String(!stackedOpen));
    } catch {
      // Storage blocked: the state still toggles for this visit.
    }
    setStackedOpen(!stackedOpen);
  };

  useEffect(() => {
    if (bundleId) dispatch(fetchObjectiveBundle(bundleId));
  }, [dispatch, bundleId]);

  // The bar's newest build shows at once; the fetch confirms it.
  const bundle =
    currentBundle.data?.id === bundleId
      ? currentBundle.data
      : build.data?.id === bundleId
        ? build.data
        : undefined;
  const view = bundle?.view ?? "simple";
  const squadRun = bundle?.squads?.[view];
  const squadsRunning = squadRun?.status === "running";
  const buildingIndex = squadRun?.blocks.findIndex((b) => b === null);

  // Squads land on the bundle one by one while a run is going; each poll
  // brings only the blocks not here yet.
  useEffect(() => {
    if (!squadsRunning || !bundleId) return;
    const timer = setInterval(
      () => dispatch(pollObjectiveSquads({ id: bundleId, view })),
      SQUAD_POLL_MS,
    );
    return () => clearInterval(timer);
  }, [dispatch, bundleId, squadsRunning, view]);

  // The stacked plan is slow to solve, so it is built only when viewed, and
  // again after a rebuild dropped it.
  useEffect(() => {
    if (
      view === "stacked" &&
      bundle &&
      !bundle.stackedPlan &&
      currentBundle.stackedPlanStatus === "idle"
    )
      dispatch(fetchStackedPlan(bundle.id));
  }, [dispatch, view, bundle, currentBundle.stackedPlanStatus]);

  const shownPlan =
    view === "stacked" && bundle?.stackedPlan
      ? bundle.stackedPlan
      : bundle?.simplePlan;
  // The simple plan covers the picked groups and those their references pull
  // in; the stacked plan every group, partial ones included.
  const groups = !bundle
    ? []
    : view === "stacked" && bundle.stackedPlan
      ? bundle.groups
      : bundle.groups.filter(
          (g) => bundle.groupIds.includes(g.id) || g.expandedFrom !== null,
        );
  // The bundle as the user built it: the picked groups, then the ones they
  // require. The same in both views.
  const addedGroupIds = new Set(bundle?.stackedPlan?.addedGroupIds);
  const bundleGroups = !bundle
    ? []
    : [
        ...bundle.groupIds.flatMap(
          (id) => bundle.groups.find((g) => g.id === id) ?? [],
        ),
        ...bundle.groups.filter(
          (g) =>
            g.expandedFrom !== null &&
            !bundle.groupIds.includes(g.id) &&
            !addedGroupIds.has(g.id),
        ),
      ];
  const title =
    bundle && shownPlan
      ? t("plan.titleSummary", {
          summary: [
            t("bar.groups", { count: bundleGroups.length }),
            t("bar.games", { count: shownPlan.nMatches }),
          ].join(" - "),
        })
      : t("plan.title");

  const content = (() => {
    if (!bundle)
      return (
        <div className={cn(CONTAINER, "pt-4")}>
          {currentBundle.status === "rejected" ? (
            <p className="text-center text-sm text-foreground-muted">
              {t("plan.notFound")}
            </p>
          ) : (
            <div className="m-auto">
              <Spinner />
            </div>
          )}
        </div>
      );

    // The first switch to Stacked after a build solves it; until then there
    // is nothing below the toolbar to show for that view.
    const stackedPending = view === "stacked" && !bundle.stackedPlan;
    const stackedPlan = view === "stacked" ? bundle.stackedPlan : undefined;
    const plan = stackedPlan ?? bundle.simplePlan;
    // The user's counts, [game][objective row]; missing ones are 0.
    const counts = bundle.progress[view] ?? [];
    const countAt = (game: number, row: number) => counts[game]?.[row] ?? 0;
    const setCounts = (game: number, values: number[]) => {
      const progress = Array.from({ length: plan.nMatches }, (_, g) =>
        g === game ? values : Array.from(counts[g] ?? [], (v) => v ?? 0),
      );
      dispatch(changeObjectiveProgress({ id: bundle.id, view, progress }));
    };
    const setCount = (game: number, row: number, value: number) =>
      setCounts(
        game,
        Array.from(
          { length: Math.max(row + 1, counts[game]?.length ?? 0) },
          (_, r) => (r === row ? value : countAt(game, r)),
        ),
      );
    const addedObjectiveIds = new Set(stackedPlan?.addedObjectiveIds);
    const shownGroupIds = new Set(groups.map((g) => g.id));
    const challenges = bundle.objectives.filter((o) =>
      shownGroupIds.has(o.groupId),
    );
    const groupName = (id: number) =>
      bundle.groups.find((g) => g.id === id)?.name ??
      t("bar.groupFallback", { id });
    // Controls wait while a rebuild runs, so two rebuilds never race on the
    // server.
    const rebuilding = build.status === "pending";
    const includeLoanPlayers = bundle.includeLoanPlayers;
    // Apply rebuilds at once; the rebuilt plan is hidden until revealed.
    const applySettings = (settings: PlanSettings) =>
      dispatch(
        changeObjectiveBundle({
          type: "set",
          bundle: {
            groupIds: bundle.groupIds,
            ...settings,
            includeLoanPlayers,
          },
        }),
      );
    const squadFailedReason = (code: string) =>
      code === "NO_TACTIC_FOR_FORMATION"
        ? t("plan.squads.noTactic")
        : code === "EVENT_SQUAD_INFEASIBLE" ||
            code === "GAUNTLET_SQUADS_INFEASIBLE"
          ? errorMessage(code, "solver")
          : t("plan.squads.failed");
    const liveEventName = (id: number) =>
      bundle.liveEvents.find((e) => e.id === id)?.name ??
      t("plan.entry.eventFallback", { id });
    // "Fri, Oct 2, 7PM" in 12-hour locales, "Fr., 2. Okt., 19:00" in 24-hour ones.
    const resetDate = (seconds: number) => {
      const date = new Date(seconds * 1000);
      const h12 = new Intl.DateTimeFormat(i18n.language, {
        hour: "numeric",
      }).resolvedOptions().hour12;
      const parts = new Intl.DateTimeFormat(i18n.language, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: h12 ? "numeric" : "2-digit",
        minute: h12 && date.getMinutes() === 0 ? undefined : "2-digit",
        hourCycle: h12 ? undefined : "h23",
      }).formatToParts(date);
      return parts
        .filter(
          (p, i) =>
            !(p.type === "literal" && parts[i + 1]?.type === "dayPeriod"),
        )
        .map((p) => p.value)
        .join("");
    };
    const period = (interval: number) =>
      interval === 86400 ? ("day" as const) : ("week" as const);
    // A repeat completion still open once this plan's games are played.
    const nextStage = bundle.recurring.find(
      (r) => r.inPlan && r.done + 1 < r.reachable && r.nextResetAt,
    );
    // Nothing in this window: the repeat waits for its reset.
    const waiting = bundle.recurring.find(
      (r) => !r.inPlan && r.done < r.reachable && r.nextResetAt,
    );
    const sideTasks = new Map<string, Challenge[]>();
    for (const o of challenges.filter((o) => o.status === "side-task"))
      sideTasks.set(o.kind ?? "other", [
        ...(sideTasks.get(o.kind ?? "other") ?? []),
        o,
      ]);
    const notPlannable = challenges.filter((o) =>
      NOT_PLANNABLE.some((s) => s === o.status),
    );
    const completed = challenges.filter((o) => o.status === "completed");
    // Challenges their group doesn't need; the stacked plan's extras sit in the games instead.
    const notNeeded = challenges.filter(
      (o) => o.status === "optional" && !addedObjectiveIds.has(o.id),
    );

    const challengeLine = (
      challenge: Challenge,
      children?: React.ReactNode,
    ) => (
      <div key={challenge.id} className="flex flex-col gap-y-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-foreground">
            {challenge.name ?? challenge.id}
          </span>
          <span className="text-foreground-muted">
            {groupName(challenge.groupId)}
          </span>
          {addedObjectiveIds.has(challenge.id) && (
            <span className={cn(TAG, "bg-emerald-900/50 text-emerald-400")}>
              {t("plan.extra")}
            </span>
          )}
          {challenge.partial && (
            <span className={cn(TAG, "bg-amber-900/50 text-amber-400")}>
              {t("plan.partial")}
            </span>
          )}
        </div>
        {children}
      </div>
    );

    // Games are numbered across the plan; `[game][row]` indexes the counts.
    let game = 0;
    // Gauntlet games played so far per live event; every `roundSize` of them is a round.
    const gauntletGames = new Map<number | null, number>();
    // Live events whose entry win is already on a game, from an objective's win or an earlier game.
    const entryWins = new Set(
      plan.blocks
        .filter((b) =>
          b.parts.some((p) => p.objectives.some((o) => o.contribution.win)),
        )
        .map((b) => b.eventId),
    );
    const blocks = plan.blocks.map((block) => {
      const from = game + 1;
      // Before the reveal a block has no parts, only its count.
      const parts = bundle.revealed
        ? block.parts
        : [{ count: block.count, objectives: [] }];
      const games = parts.flatMap((part) => {
        // A qualifying match that ticks no objective: it only unlocks live events.
        const actions =
          part.objectives.length === 0 && block.unlocks.length > 0
            ? [{ text: t("action.win") }]
            : blockActions(part.objectives, t);
        return Array.from({ length: part.count }, () => {
          const index = game++;
          // Another live event's entry needs one game here won, so the first one says Win.
          const mustWin =
            block.eventId !== null &&
            !!block.winUnlocks?.length &&
            !entryWins.has(block.eventId);
          if (mustWin) entryWins.add(block.eventId!);
          const win = t("action.win");
          const gameActions =
            mustWin && !actions.some((a) => a.text === win)
              ? [
                  { text: win },
                  ...actions.filter((a) => a.text !== t("action.play")),
                ]
              : actions;
          const rows = gameActions.map((action, r) => {
            const target = action.count ?? 1;
            const value = countAt(index, r);
            return { ...action, r, target, value, done: value >= target };
          });
          let title = t("plan.game", { n: index + 1 });
          let round: number | undefined;
          // A Gauntlet game plays its block's squad at its slot in the round.
          let slot = 0;
          if (block.roundSize) {
            const k = gauntletGames.get(block.eventId) ?? 0;
            gauntletGames.set(block.eventId, k + 1);
            round = Math.floor(k / block.roundSize) + 1;
            slot = k % block.roundSize;
            title = t("plan.game", { n: slot + 1 });
          }
          return {
            index,
            title,
            round,
            slot,
            rows,
            done: rows.every((row) => row.done),
          };
        });
      });
      return { block, from, to: game, games, done: games.every((g) => g.done) };
    });
    const allGamesDone = blocks.length > 0 && blocks.every((b) => b.done);
    // Deleting the bundle resets Customize too, then the list is next.
    const completePlan = () => {
      dispatch(changeObjectiveBundle({ type: "set", bundle: DEFAULT_BUNDLE }));
      navigate("/objectives");
    };
    // The first block not completed opens by default.
    const currentKey = `${view}-${blocks.findIndex((b) => !b.done)}`;
    const blockOpen =
      blockToggles.current === currentKey ? blockToggles.open : {};
    // The first game not completed opens by default.
    const currentGame = blocks
      .flatMap((b) => b.games)
      .find((g) => !g.done)?.index;
    const currentGameKey = `${view}-${currentGame}`;
    const gameOpen =
      gameToggles.current === currentGameKey ? gameToggles.open : {};
    const squadsError =
      currentBundle.squadsErrorCode === "OBJECTIVE_SQUADS_RUNNING"
        ? t("plan.squads.alreadyRunning")
        : currentBundle.squadsErrorCode
          ? errorMessage(currentBundle.squadsErrorCode)
          : undefined;

    const saved = !!bundle.squadsSaved[view];
    const refreshing = refresh?.view === view;
    // Saves every built squad of the view, each Gauntlet squad too, over saves
    // of the same name; older "Obj: …" saves from earlier plans go. An update
    // skips completed blocks but keeps their saves.
    const saveSquads = async (update = false) => {
      if (!squadRun) return;
      setSavingSquads(true);
      try {
        const prefix = t("squad.batchPrefix");
        const existing = await getSquads();
        const names: string[] = [];
        let count = 0;
        for (const [i, entry] of squadRun.blocks.entries()) {
          const block = blocks[i];
          if (entry?.status !== "done" || !block) continue;
          const range = block.to > block.from;
          // Gauntlet names stay short to fit EA's 15-character limit.
          const key = block.block.roundSize
            ? range
              ? "squad.gauntletRange"
              : "squad.gauntletSingle"
            : range
              ? "squad.gamesRange"
              : "squad.gameSingle";
          for (const [k, squad] of entry.squads.entries()) {
            const name =
              prefix +
              t(key, {
                from: block.from,
                to: block.to,
                n: block.from,
                i: k + 1,
              });
            names.push(name);
            if (update && block.done) continue;
            count++;
            await saveSquad(
              objectiveSquadSaveRequest(
                squad,
                name,
                existing.find((s) => s.name === name)?.id,
              ),
            );
          }
        }
        for (const old of existing)
          if (old.name.startsWith(prefix) && !names.includes(old.name))
            await deleteSavedSquad(old.id);
        dispatch(markSquadsSaved({ id: bundle.id, view }));
        if (update && refreshing)
          setRefresh({ view, started: true, updated: true });
        dispatch(
          addAlert({
            type: "success",
            description: t("squad.savedAll", { count }),
          }),
        );
      } catch (error) {
        dispatch(
          addAlert({
            type: "error",
            description: errorMessage(backendErrorCode(error)),
          }),
        );
      } finally {
        setSavingSquads(false);
      }
    };
    const squadSettings = storedSquadSettings ?? {
      includeLoanPlayers: bundle.includeLoanPlayers,
      includeActiveEvos: bundle.includeActiveEvos,
      marketManagers: bundle.marketManagers,
      preferredFormation: bundle.preferredFormation,
    };
    const squadSettingKeys = [
      "includeLoanPlayers",
      "includeActiveEvos",
      "marketManagers",
      "preferredFormation",
    ] as const;
    // Built squads used other settings, so Build (or Refresh) is ready again.
    const squadSettingsChanged = squadSettingKeys.some(
      (k) => squadSettings[k] !== bundle[k],
    );
    // Pulled toward the button it sits next to; the icon brings its own padding.
    const squadSettingsButton = (
      <span className="-ml-2 inline-flex">
        <CustomizeButton
          label={t("plan.squadSettings.title")}
          bare
          width={340}
          active={squadSettingKeys.some(
            (k) =>
              k !== "preferredFormation" &&
              squadSettings[k] !== DEFAULT_SQUAD_SETTINGS[k],
          )}
          disabled={squadsRunning}
          panel={({ compact, close }) => (
            <SquadSettingsPanel
              compact={compact}
              close={close}
              settings={squadSettings}
              onChange={changeSquadSettings}
            />
          )}
        />
      </span>
    );
    const revealState: StepState = bundle.revealed
      ? "done"
      : currentBundle.revealStatus === "pending" || rebuilding
        ? "waiting"
        : "ready";
    const canSave =
      squadRun?.status === "done" &&
      !savingSquads &&
      squadRun.blocks.some((b) => b?.status === "done");
    // Build and save lock once squads were saved; later changes go through
    // the refresh row.
    const buildState: StepState = !bundle.revealed
      ? "waiting"
      : saved
        ? "done"
        : squadsRunning
          ? "waiting"
          : squadRun?.status === "done" && !squadSettingsChanged
            ? "done"
            : "ready";
    const saveState: StepState = saved ? "done" : canSave ? "ready" : "waiting";
    // Refresh rebuilds the squads of the blocks not completed yet with the club
    // as it is now; Update saves them over the old ones.
    // Refresh ticks once its run is built and stays ticked; Update saves the
    // squads as they are, refreshed or not, and ticks after a refresh.
    const refreshPending = squadsRunning || (refreshing && !refresh.started);
    const refreshState: StepState = refreshPending
      ? "waiting"
      : refreshing && !squadSettingsChanged
        ? "done"
        : "ready";
    const updateState: StepState =
      refreshPending || !canSave
        ? "waiting"
        : refreshing && refresh.updated
          ? "done"
          : "ready";
    // The in-game steps stay grey until the three buttons are done.
    const inGameText = cn(
      "text-sm",
      [revealState, buildState, saveState].every((s) => s === "done")
        ? "text-foreground-body"
        : "text-neutral-500",
    );

    const statusLine = (squadsError || currentBundle.progressFailed) && (
      <div className="flex flex-wrap items-center gap-4">
        {squadsError && (
          <span className="text-sm text-amber-400">{squadsError}</span>
        )}
        {currentBundle.progressFailed && (
          <span className="text-sm text-amber-400">
            {t("plan.progress.failed")}
          </span>
        )}
      </div>
    );

    const steps = (
      <ol className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <Step first>
          <StepButton
            state={revealState}
            magic
            onClick={() => dispatch(revealPlan(bundle.id))}
          >
            {t("plan.steps.reveal")}
          </StepButton>
        </Step>
        <Step>
          <StepButton
            state={buildState}
            magic
            onClick={() =>
              dispatch(
                requestObjectiveSquads({
                  id: bundle.id,
                  view,
                  settings: squadSettings,
                }),
              )
            }
          >
            {t("plan.squads.build")}
          </StepButton>
          {squadSettingsButton}
        </Step>
        <Step>
          <StepButton state={saveState} onClick={() => saveSquads()}>
            {t("plan.steps.save")}
          </StepButton>
        </Step>
        <Step>
          <span className={inGameText}>{t("plan.steps.insert")}</span>
          <Popover
            title={t("plan.steps.insertTitle")}
            negLabel=""
            posLabel=""
            trigger={
              <button
                type="button"
                aria-label={t("plan.steps.insert")}
                className="flex shrink-0 text-foreground-muted hover:text-foreground"
              >
                <Info className="size-4" />
              </button>
            }
          >
            <p className="text-sm text-foreground-body">
              {t("plan.steps.insertInfo")}
            </p>
          </Popover>
        </Step>
        <Step>
          <span className={inGameText}>{t("plan.steps.play")}</span>
        </Step>
        <Step>
          <span className={inGameText}>{t("plan.steps.rewards")}</span>
        </Step>
      </ol>
    );

    // Rewards: the bundle's, then what the stacked plan adds on top.
    const allAddedObjectiveIds = new Set(bundle.stackedPlan?.addedObjectiveIds);
    const bundleGroupIds = new Set(bundleGroups.map((g) => g.id));
    // Every group the stacked plan adds to, partial ones included; addedGroupIds holds only the complete ones.
    const stackedGroups = bundle.groups.filter(
      (g) => !bundle.groupIds.includes(g.id) && g.expandedFrom === null,
    );
    // Rewards this plan doesn't earn: challenges it leaves open (later weeks,
    // not plannable) and the groups holding them. A challenge the group doesn't
    // need leaves the group whole.
    const unsecured = (o: Challenge) =>
      !SECURED.some((s) => s === o.status) &&
      o.status !== "optional" &&
      !allAddedObjectiveIds.has(o.id);
    const unsecuredGroupIds = new Set(
      bundle.objectives.filter(unsecured).map((o) => o.groupId),
    );
    const groupRewards = (list: typeof bundle.groups) =>
      list.flatMap((g) =>
        g.rewards.map((r) => ({
          ...r,
          source: { group: groupName(g.id) },
          partial: bundleGroupIds.has(g.id) && unsecuredGroupIds.has(g.id),
        })),
      );
    const challengeRewards = (list: Challenge[]) =>
      list.flatMap((o) =>
        o.rewards.map((r) => ({
          ...r,
          source: {
            group: groupName(o.groupId),
            challenge: o.name ?? String(o.id),
          },
          partial: unsecured(o),
        })),
      );
    const bundleRewards = [
      ...groupRewards(bundleGroups),
      ...challengeRewards(
        bundle.objectives.filter(
          (o) =>
            bundleGroupIds.has(o.groupId) &&
            !allAddedObjectiveIds.has(o.id) &&
            o.status !== "completed" &&
            o.status !== "optional",
        ),
      ),
    ];
    // A partial group pays only its challenges.
    const extraRewards =
      view === "stacked"
        ? [
            ...groupRewards(
              stackedGroups.filter((g) => g.status !== "partial"),
            ),
            ...challengeRewards(
              bundle.objectives.filter((o) => allAddedObjectiveIds.has(o.id)),
            ),
          ]
        : [];

    // Removing the last group deletes the bundle, so the page goes back.
    const removeGroup = (groupId: number) => {
      const groupIds = bundle.groupIds.filter((id) => id !== groupId);
      dispatch(
        changeObjectiveBundle({
          type: "set",
          bundle: {
            groupIds,
            includeLoanPlayers,
            excludedModes: bundle.excludedModes,
          },
        }),
      );
      if (groupIds.length === 0) goBack();
    };
    // The bundle expires with its first group; 0 never expires.
    const expiries = bundleGroups.flatMap((g) =>
      g.endTime ? [g.endTime] : [],
    );
    const completeBy = expiries.length > 0 ? Math.min(...expiries) : undefined;

    return (
      <>
        {allGamesDone && !completionDismissed && (
          <div
            className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto bg-black/60 px-4 py-8 backdrop-blur-[2px] transition-opacity duration-200 starting:opacity-0"
            onClick={() => setCompletionDismissed(true)}
          >
            <div
              className="relative mx-auto flex w-full max-w-[520px] flex-col items-center gap-4 rounded-2xl border border-streamline-current-border bg-streamline-current-surface px-6 py-8 text-center transition-[opacity,transform] duration-200 ease-out starting:scale-95 starting:opacity-0"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="absolute right-3 top-3">
                <CloseButton onClick={() => setCompletionDismissed(true)} />
              </div>
              <Trophy className="size-10 text-streamline-success" />
              <div className="flex flex-col gap-1">
                <span className="text-2xl font-extrabold text-white">
                  {nextStage
                    ? t(
                        `plan.recurring.${period(nextStage.interval)}.doneTitle`,
                      )
                    : t("plan.completion.title")}
                </span>
                <span className="whitespace-pre-line text-sm font-semibold text-neutral-500">
                  {nextStage
                    ? t("plan.recurring.nextStage", {
                        done: nextStage.done + 1,
                        target: nextStage.target,
                        date: resetDate(nextStage.nextResetAt!),
                      })
                    : t("plan.completion.body")}
                </span>
              </div>
              <Button
                variant="primary"
                size="medium"
                disabled={rebuilding}
                onClick={completePlan}
              >
                {t("plan.completion.markComplete")}
              </Button>
              <span className="text-xs italic text-neutral-500">
                {t("plan.completion.syncHint")}
              </span>
            </div>
          </div>
        )}
        <div className={cn(CONTAINER, "pb-6 pt-2")}>
          <div className="grid grid-cols-2 gap-3 desktop:grid-cols-3 large:grid-cols-4">
            {bundleGroups.map((g) => {
              const picked = bundle.groupIds.includes(g.id);
              return (
                <BundleGroupCard
                  key={g.id}
                  group={g}
                  dim={!picked}
                  onRemove={
                    picked && !rebuilding ? () => removeGroup(g.id) : undefined
                  }
                  note={
                    !picked && g.expandedFrom !== null
                      ? t("plan.requiredFor", {
                          name: groupName(g.expandedFrom),
                        })
                      : undefined
                  }
                  tag={
                    g.completed && (
                      <span
                        className={cn(
                          TAG,
                          "self-start bg-emerald-900/50 text-emerald-400",
                        )}
                      >
                        {t("bundle.completed")}
                      </span>
                    )
                  }
                />
              );
            })}
          </div>
        </div>

        <Toolbar color="bg-surface">
          <div className="flex w-full min-w-0 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-x-3">
              <div className="min-w-0">
                <ClubImportRow
                  isTouch={isTouch}
                  origin={CLUB_IMPORT_ORIGIN}
                  importLabel={t("plan.importClub")}
                  importedLabel={t("plan.clubImported")}
                  pitch={t("plan.clubPitch")}
                />
              </div>
              <CustomizeButton
                label={t("plan.customize")}
                compact={isTouch}
                width={420}
                active={hasCustomPlanSettings(bundle)}
                disabled={rebuilding}
                panel={({ compact, close }) => (
                  <PlanSettingsPanel
                    compact={compact}
                    close={close}
                    settings={{ excludedModes: bundle.excludedModes }}
                    onApply={applySettings}
                  />
                )}
              />
              <SegmentedControl
                label={t("plan.view.label")}
                value={view}
                onChange={(next) =>
                  dispatch(changeObjectiveView({ id: bundle.id, view: next }))
                }
                options={[
                  { value: "simple", label: t("plan.view.simple") },
                  {
                    value: "stacked",
                    label: (
                      <span className="flex items-center gap-x-1.5">
                        {stackedPending &&
                          currentBundle.stackedPlanStatus === "pending" && (
                            <LoaderCircle className="size-3 animate-spin" />
                          )}
                        <span className="text-holo">
                          {t("plan.view.stacked")}
                        </span>
                      </span>
                    ),
                  },
                ]}
              />
              <InfoTip label={t("plan.view.stacked")}>
                {t("plan.view.stackedInfo")}
              </InfoTip>
            </div>
          </div>
        </Toolbar>

        <div className={cn(CONTAINER, "flex flex-col gap-y-8 pb-12 pt-6")}>
          {stackedPending ? (
            currentBundle.stackedPlanStatus !== "rejected" && (
              <div className="mx-auto">
                <Spinner />
              </div>
            )
          ) : (
            <>
              {/* Open by default: the keys mark them closed. */}
              <BundleRewards
                label={t("plan.rewards")}
                open={!open.has("rewards-closed")}
                onToggle={() => toggleOpen("rewards-closed")}
                rewards={bundleRewards}
              />
              <BundleRewards
                label={t("plan.rewardsStacked")}
                open={!open.has("stacked-rewards-closed")}
                onToggle={() => toggleOpen("stacked-rewards-closed")}
                rewards={extraRewards}
              />
            </>
          )}

          <Foldout
            open={stackedOpen}
            onToggle={toggleStackedOpen}
            label={
              <>
                {t("plan.stacked.title")}
                <span
                  className={cn(
                    "flex h-5 min-w-5 items-center justify-center rounded-full bg-neutral-800 px-1.5 text-xs font-semibold",
                    view === "simple"
                      ? "text-foreground-muted"
                      : "text-foreground",
                  )}
                >
                  {stackedPending ? (
                    <LoaderCircle className="size-3 animate-spin" />
                  ) : (
                    <span>{view === "simple" ? 0 : stackedGroups.length}</span>
                  )}
                </span>
              </>
            }
          >
            {view === "simple" ? (
              <p className="text-sm text-foreground-muted">
                {t("plan.stacked.hint")}
              </p>
            ) : stackedPending ? (
              currentBundle.stackedPlanStatus === "rejected" && (
                <p className="text-sm text-foreground-muted">
                  {t("plan.stackedPlanFailed")}
                </p>
              )
            ) : stackedGroups.length === 0 ? (
              <p className="text-sm text-foreground-muted">
                {t("plan.stacked.none")}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3 desktop:grid-cols-3 large:grid-cols-4">
                {stackedGroups.map((g) => (
                  <BundleGroupCard
                    key={g.id}
                    group={g}
                    tag={
                      <span
                        className={cn(
                          TAG,
                          "self-start",
                          g.status === "partial"
                            ? "bg-amber-900/50 text-amber-400"
                            : "bg-emerald-900/50 text-emerald-400",
                        )}
                      >
                        {g.status === "partial"
                          ? t("plan.stacked.partial")
                          : t("plan.stacked.complete")}
                      </span>
                    }
                  />
                ))}
              </div>
            )}
          </Foldout>

          {!stackedPending && (
            <>
              <section className="flex flex-col gap-y-3">
                <div className="flex flex-col gap-y-2">
                  <span className={FOLDOUT_HEADING}>{t("plan.whatsNext")}</span>
                  {steps}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <h2 className="text-lg font-semibold text-foreground">
                      {t("plan.gamePlan")}
                    </h2>
                    {completeBy && (
                      <span className="text-sm text-foreground-muted">
                        {t("card.expiresIn", {
                          time: formatTime(completeBy * 1000 - Date.now()),
                        })}
                      </span>
                    )}
                  </div>
                  {saved && (
                    <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                      <Step first small>
                        <StepButton
                          small
                          state={refreshState}
                          onClick={() => {
                            setRefresh({
                              view,
                              started: false,
                              updated: false,
                            });
                            dispatch(
                              requestObjectiveSquads({
                                id: bundle.id,
                                view,
                                settings: squadSettings,
                                // Completed blocks are played; their squads stay.
                                keepBlocks: blocks.flatMap((b, i) =>
                                  b.done ? [i] : [],
                                ),
                              }),
                            ).then((result) =>
                              setRefresh(
                                requestObjectiveSquads.rejected.match(result)
                                  ? undefined
                                  : {
                                      view,
                                      started: true,
                                      updated: false,
                                    },
                              ),
                            );
                          }}
                        >
                          {t("plan.refresh.refresh")}
                        </StepButton>
                        {squadSettingsButton}
                      </Step>
                      <Step small>
                        <StepButton
                          small
                          state={updateState}
                          onClick={() => saveSquads(true)}
                        >
                          {t("plan.refresh.update")}
                        </StepButton>
                      </Step>
                      <Step small>
                        <span className="text-xs text-foreground-body">
                          {t("plan.refresh.updateInWebApp")}
                        </span>
                        <Popover
                          title={t("plan.refresh.updateTitle")}
                          negLabel=""
                          posLabel=""
                          trigger={
                            <button
                              type="button"
                              aria-label={t("plan.refresh.updateInWebApp")}
                              className="flex shrink-0 text-foreground-muted hover:text-foreground"
                            >
                              <Info className="size-3.5" />
                            </button>
                          }
                        >
                          <p className="text-sm text-foreground-body">
                            {t("plan.refresh.updateInfo")}
                          </p>
                        </Popover>
                      </Step>
                    </ol>
                  )}
                </div>
                {statusLine}
                {plan.blocks.length === 0 && (
                  <p className="text-sm text-foreground-muted">
                    {waiting
                      ? t("plan.recurring.waiting", {
                          name: waiting.name ?? waiting.groupId,
                          date: resetDate(waiting.nextResetAt!),
                        })
                      : t("plan.noGames")}
                  </p>
                )}
                {blocks.map(({ block, from, to, games, done }, blockIndex) => {
                  const expanded =
                    blockOpen[blockIndex] ??
                    currentKey === `${view}-${blockIndex}`;
                  const mode = MODES.find((m) => m === block.mode);
                  const modeLabel = mode ? t(`mode.${mode}`) : block.mode;
                  // Before the reveal a block is only its title over blurred rows.
                  if (!bundle.revealed)
                    return (
                      <Card
                        key={blockIndex}
                        hoverable={false}
                        className="flex flex-col gap-y-3 p-4"
                      >
                        <h3 className="text-base font-semibold text-foreground">
                          {block.count > 1
                            ? t("plan.blockRange", {
                                from,
                                to,
                                mode: modeLabel,
                              })
                            : t("plan.blockSingle", { from, mode: modeLabel })}
                        </h3>
                        <div
                          aria-hidden
                          className="pointer-events-none flex select-none flex-col gap-y-2 blur-sm"
                        >
                          {games.map(({ index, title, round }, g) => (
                            <div key={index} className="flex flex-col gap-y-1">
                              {round !== undefined &&
                                round !== games[g - 1]?.round && (
                                  <span className={FOLDOUT_HEADING}>
                                    {t("plan.gauntletRound", { round })}
                                  </span>
                                )}
                              <span className="text-sm font-semibold text-foreground">
                                {title}
                              </span>
                              <span className="pl-7 text-sm text-foreground-body">
                                {t("plan.hiddenRow")}
                              </span>
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  const difficulty = DIFFICULTIES.find(
                    (d) => d === block.difficulty,
                  );
                  const formation = block.lineup.formation;
                  const rules = eventSquadLines(block.lineup.eventSquad, t);
                  const rounds = roundLines(block.parts, t);
                  // "Any formation" alone says nothing, so it gets no caret.
                  const requirements =
                    formation ||
                    block.lineup.starting.length > 0 ||
                    block.lineup.anywhere.length > 0 ||
                    rounds.length > 0 ||
                    rules.length > 0
                      ? [
                          formation
                            ? (formations[formation]?.label ?? formation)
                            : t("plan.anyFormation"),
                          ...lineupLines(block.lineup, t),
                          ...rounds,
                          ...rules,
                        ]
                      : [];
                  const noteShown = !!block.lineup.eventSquad?.stringEntry;
                  const rulesKey = `${view}-rules-${blockIndex}`;
                  const modeOptions = block.modeOptions ?? [];
                  const modesKey = `${view}-modes-${blockIndex}`;
                  return (
                    <Card
                      key={blockIndex}
                      hoverable={false}
                      className="flex flex-col p-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <button
                          type="button"
                          aria-expanded={expanded}
                          onClick={() =>
                            setBlockToggles({
                              current: currentKey,
                              open: { ...blockOpen, [blockIndex]: !expanded },
                            })
                          }
                          className="flex flex-wrap items-center gap-x-1 text-left"
                        >
                          <h3
                            className={cn(
                              "text-base font-semibold",
                              done
                                ? "text-foreground-muted line-through"
                                : "text-foreground",
                            )}
                          >
                            {block.count > 1
                              ? t("plan.blockRange", {
                                  from,
                                  to,
                                  mode: modeLabel,
                                })
                              : t("plan.blockSingle", {
                                  from,
                                  mode: modeLabel,
                                })}
                          </h3>
                          {!done && (
                            <span className="ml-2 text-sm text-foreground-muted">
                              {[
                                difficulty && t(`difficulty.${difficulty}`),
                                block.event,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </span>
                          )}
                          <ChevronDown
                            size={16}
                            className={cn(
                              "shrink-0 text-foreground-muted transition-transform duration-200",
                              !expanded && "-rotate-90",
                            )}
                          />
                        </button>
                        {squadRun && (
                          <div className="flex items-center gap-x-3 text-sm">
                            {(() => {
                              const entry = squadRun.blocks[blockIndex];
                              if (entry?.status === "done")
                                return (
                                  <Button
                                    variant="secondary"
                                    size="small"
                                    disabled={done}
                                    // Opens on the first unfinished game's squad.
                                    onClick={() =>
                                      setShownSquads({
                                        squads: entry.squads,
                                        index:
                                          (
                                            games.find((g) => !g.done) ??
                                            games[0]
                                          )?.slot ?? 0,
                                      })
                                    }
                                  >
                                    {t(
                                      block.roundSize
                                        ? "plan.squads.showMany"
                                        : "plan.squads.show",
                                    )}
                                  </Button>
                                );
                              if (entry?.status === "failed")
                                return (
                                  <span className="text-amber-400">
                                    {squadFailedReason(entry.code)}
                                  </span>
                                );
                              if (!squadsRunning) return null;
                              // One block at a time: the first without a squad is
                              // the one building, the rest wait their turn.
                              return blockIndex === buildingIndex ? (
                                <Solving
                                  label={t(
                                    block.roundSize
                                      ? "plan.squads.buildingMany"
                                      : "plan.squads.building",
                                  )}
                                  textClassName="whitespace-nowrap text-sm"
                                />
                              ) : (
                                <div className="flex items-center gap-3">
                                  <ChemistryIcon
                                    chemistry={0}
                                    className="w-7 shrink-0"
                                  />
                                  <span className="whitespace-nowrap text-sm font-semibold text-foreground-muted">
                                    {t("plan.squads.pending")}
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      <Collapsible open={expanded}>
                        <div className="flex flex-col gap-y-3 pt-3">
                          {requirements.length > 0 && (
                            <Expander
                              label={t("plan.squadRequirements", {
                                count: requirements.length,
                              })}
                              open={open.has(rulesKey)}
                              onToggle={() => toggleOpen(rulesKey)}
                            >
                              <ul className="flex flex-col gap-y-1 text-sm text-foreground-body">
                                {requirements.map((line, i) => (
                                  <li
                                    key={i}
                                    className={cn(
                                      noteShown &&
                                        i === requirements.length - 1 &&
                                        "text-foreground-muted",
                                    )}
                                  >
                                    {line}
                                  </li>
                                ))}
                              </ul>
                            </Expander>
                          )}
                          {modeOptions.length > 1 && (
                            <Expander
                              label={t("plan.alternativeModes", {
                                count: modeOptions.length - 1,
                              })}
                              open={open.has(modesKey)}
                              onToggle={() => toggleOpen(modesKey)}
                            >
                              <div className="flex flex-col gap-y-2">
                                {modeOptions.map((option) => {
                                  const optionMode = MODES.find(
                                    (m) => m === option.mode,
                                  );
                                  const optionDifficulty = DIFFICULTIES.find(
                                    (d) => d === option.difficulty,
                                  );
                                  return (
                                    <TickButton
                                      key={option.mode}
                                      checked={option.mode === block.mode}
                                      disabled={done}
                                      onClick={() =>
                                        option.mode !== block.mode &&
                                        dispatch(
                                          changeBlockMode({
                                            id: bundle.id,
                                            view,
                                            block: blockIndex,
                                            mode: option.mode,
                                          }),
                                        )
                                      }
                                      className="text-foreground-body"
                                    >
                                      {[
                                        optionMode
                                          ? t(`mode.${optionMode}`)
                                          : option.mode,
                                        optionDifficulty &&
                                          t(`difficulty.${optionDifficulty}`),
                                      ]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </TickButton>
                                  );
                                })}
                              </div>
                            </Expander>
                          )}
                          {block.lineup.cosmetics?.map((cosmetic) => (
                            <div
                              key={cosmetic.name}
                              className="flex items-center gap-x-2 text-sm text-amber-400"
                            >
                              <TriangleAlert className="size-4 shrink-0" />
                              <span>
                                {t("plan.equip", { name: cosmetic.name })}
                              </span>
                              <InfoTip label={cosmetic.name}>
                                {cosmetic.objectiveIds.map((id) => (
                                  <span key={id} className="block">
                                    {
                                      bundle.objectives.find((o) => o.id === id)
                                        ?.description
                                    }
                                  </span>
                                ))}
                              </InfoTip>
                            </div>
                          ))}
                          <ol className="flex flex-col divide-y divide-neutral-800">
                            {games.map(
                              ({ index, title, round, rows, done }, g) => {
                                const shown =
                                  gameOpen[index] ?? index === currentGame;
                                return (
                                  <li
                                    key={index}
                                    className="flex flex-col gap-y-2 py-2"
                                  >
                                    {round !== undefined &&
                                      round !== games[g - 1]?.round && (
                                        <span
                                          className={cn(
                                            FOLDOUT_HEADING,
                                            g > 0 && "pt-3",
                                          )}
                                        >
                                          {t("plan.gauntletRound", { round })}
                                        </span>
                                      )}
                                    <div className="flex items-center gap-x-3">
                                      <button
                                        type="button"
                                        aria-pressed={done}
                                        aria-label={title}
                                        disabled={rebuilding}
                                        onClick={() =>
                                          setCounts(
                                            index,
                                            rows.map((row) =>
                                              done ? 0 : row.target,
                                            ),
                                          )
                                        }
                                        className="flex size-5 shrink-0 items-center justify-center disabled:opacity-50"
                                      >
                                        <CheckBox
                                          checked={done}
                                          className={cn(
                                            !done && "border-neutral-600",
                                          )}
                                        />
                                      </button>
                                      <button
                                        type="button"
                                        aria-expanded={shown}
                                        disabled={rows.length === 0}
                                        onClick={() =>
                                          setGameToggles({
                                            current: currentGameKey,
                                            open: {
                                              ...gameOpen,
                                              [index]: !shown,
                                            },
                                          })
                                        }
                                        className={cn(
                                          "flex items-center gap-x-1 text-left text-sm font-semibold",
                                          done
                                            ? "text-foreground-muted hover:text-foreground-body"
                                            : "text-foreground",
                                        )}
                                      >
                                        {title}
                                        {rows.length > 0 && (
                                          <ChevronDown
                                            size={16}
                                            className={cn(
                                              "text-foreground-muted transition-transform duration-200",
                                              !shown && "-rotate-90",
                                            )}
                                          />
                                        )}
                                      </button>
                                    </div>
                                    {shown && (
                                      <ul
                                        className={cn(
                                          "flex flex-col gap-y-2 pl-7",
                                          done && "opacity-60",
                                        )}
                                      >
                                        {rows.map((row) => (
                                          <li
                                            key={row.r}
                                            className="flex flex-wrap items-center gap-x-3 gap-y-1"
                                          >
                                            <TickButton
                                              checked={row.done}
                                              disabled={rebuilding}
                                              onClick={() =>
                                                setCount(
                                                  index,
                                                  row.r,
                                                  row.done ? 0 : row.target,
                                                )
                                              }
                                              className="text-foreground-body"
                                            >
                                              <span
                                                className={cn(
                                                  row.perMatch &&
                                                    "underline underline-offset-4",
                                                )}
                                              >
                                                {row.text}
                                              </span>
                                            </TickButton>
                                            {row.required !== undefined && (
                                              <InfoTip
                                                label={t(
                                                  "plan.perMatchRequired",
                                                  {
                                                    count: row.required,
                                                  },
                                                )}
                                              >
                                                {t("plan.perMatchRequired", {
                                                  count: row.required,
                                                })}
                                              </InfoTip>
                                            )}
                                            {row.count !== undefined && (
                                              <Stepper
                                                value={row.value}
                                                done={row.done}
                                                canDecrease={
                                                  !rebuilding && row.value > 0
                                                }
                                                canIncrease={
                                                  !rebuilding && !row.done
                                                }
                                                onChange={(value) =>
                                                  setCount(index, row.r, value)
                                                }
                                              />
                                            )}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                );
                              },
                            )}
                          </ol>
                        </div>
                      </Collapsible>
                    </Card>
                  );
                })}
              </section>

              {bundle.recurring.length > 0 && (
                <Section
                  title={t(
                    `plan.recurring.${period(bundle.recurring[0].interval)}.title`,
                  )}
                >
                  {bundle.recurring.map((r) => {
                    const p = period(r.interval);
                    const more = r.reachable - r.done - (r.inPlan ? 1 : 0);
                    return (
                      <Card
                        key={r.groupId}
                        hoverable={false}
                        className="flex flex-col gap-y-2 p-4"
                      >
                        <h3 className="text-base font-semibold text-foreground">
                          {r.name ?? r.groupId}
                        </h3>
                        <div className="flex items-center justify-between gap-4 text-sm">
                          <span className="text-foreground">
                            {t("plan.recurring.completions", {
                              done: r.done,
                              target: r.target,
                            })}
                          </span>
                          <span className="flex gap-1.5">
                            {Array.from({ length: r.target }, (_, i) => (
                              <span
                                key={i}
                                className={cn(
                                  "size-3 rounded-full border border-neutral-500",
                                  i < r.done &&
                                    "border-streamline-success bg-streamline-success",
                                )}
                              />
                            ))}
                          </span>
                        </div>
                        <p className="text-xs text-foreground-muted">
                          {[
                            r.done < r.target &&
                              t(
                                `plan.recurring.${p}.${r.inPlan ? "inPlan" : "done"}`,
                              ),
                            more > 0 &&
                              t(`plan.recurring.${p}.more`, { count: more }),
                            more > 0 &&
                              r.nextResetAt &&
                              t("plan.recurring.nextReset", {
                                date: resetDate(r.nextResetAt),
                              }),
                            r.endTime &&
                              t("plan.recurring.ends", {
                                date: resetDate(r.endTime),
                              }),
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        </p>
                        {r.reachable < r.target && (
                          <p className="text-xs text-amber-400">
                            {t(`plan.recurring.${p}.short`, {
                              count: r.reachable - r.done,
                              needed: r.target - r.done,
                              reachable: r.reachable,
                              target: r.target,
                            })}
                          </p>
                        )}
                      </Card>
                    );
                  })}
                </Section>
              )}

              {sideTasks.size > 0 && (
                <Section title={t("plan.sideTasks")}>
                  {[...sideTasks].map(([kind, tasks]) => {
                    const known = KINDS.find((k) => k === kind);
                    return (
                      <Card
                        key={kind}
                        hoverable={false}
                        className="flex flex-col gap-y-3 p-4"
                      >
                        <h3 className="text-base font-semibold text-foreground">
                          {known ? t(`kind.${known}`) : kind}
                        </h3>
                        {tasks.map((challenge) =>
                          challengeLine(
                            challenge,
                            <p className="text-xs text-foreground-muted">
                              {challenge.description}
                            </p>,
                          ),
                        )}
                      </Card>
                    );
                  })}
                </Section>
              )}

              {notPlannable.length > 0 && (
                <Section title={t("plan.notPlannable")}>
                  <Card hoverable={false} className="flex flex-col gap-y-3 p-4">
                    {notPlannable.map((challenge) => {
                      const status = NOT_PLANNABLE.find(
                        (s) => s === challenge.status,
                      );
                      return challengeLine(
                        challenge,
                        <>
                          <p className="text-xs text-foreground-muted">
                            {challenge.description}
                          </p>
                          {status && (
                            <p className="text-xs text-amber-400">
                              {challenge.entry
                                ? t(
                                    `plan.reason.entry.${challenge.entry.kind}`,
                                    {
                                      event: liveEventName(
                                        challenge.entry.eventId,
                                      ),
                                    },
                                  )
                                : t(`plan.reason.${status}`)}
                            </p>
                          )}
                        </>,
                      );
                    })}
                  </Card>
                </Section>
              )}

              {notNeeded.length > 0 && (
                <Section title={t("plan.notNeeded")}>
                  <Card hoverable={false} className="flex flex-col gap-y-3 p-4">
                    {notNeeded.map((challenge) => {
                      const group = bundle.groups.find(
                        (g) => g.id === challenge.groupId,
                      );
                      return challengeLine(
                        challenge,
                        <>
                          <p className="text-xs text-foreground-muted">
                            {challenge.description}
                          </p>
                          {group?.nObjectivesRequired !== undefined && (
                            <p className="text-xs text-foreground-faint">
                              {t("plan.reason.optional", {
                                group: groupName(group.id),
                                required: group.nObjectivesRequired,
                                count: group.nObjectives,
                              })}
                            </p>
                          )}
                        </>,
                      );
                    })}
                  </Card>
                </Section>
              )}

              {completed.length > 0 && (
                <Section title={t("plan.completed")}>
                  <Card hoverable={false} className="flex flex-col gap-y-3 p-4">
                    {completed.map((challenge) =>
                      challengeLine(
                        challenge,
                        <p className="text-xs text-foreground-muted">
                          {challenge.description}
                        </p>,
                      ),
                    )}
                  </Card>
                </Section>
              )}
            </>
          )}
        </div>
      </>
    );
  })();

  return (
    <main className="min-h-screen bg-background">
      <HeroSection
        helmetTitle={title}
        back={goBack}
        shouldBeInTheDocumentFlow
        hidePageDescription
      />
      {content}
      <BottomSheet
        isOpen={!!shownSquads}
        onClose={() => setShownSquads(undefined)}
        className="max-h-[85dvh] max-w-content bg-neutral-900"
      >
        {shownSquads && (
          <ObjectiveSquadView
            squads={shownSquads.squads}
            initialIndex={shownSquads.index}
            onClose={() => setShownSquads(undefined)}
          />
        )}
      </BottomSheet>
    </main>
  );
};

export default ObjectivePlanPage;
